import _extends from "@babel/runtime/helpers/extends";
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useFela } from 'react-fela';
import { getLinkRel } from '../../utils/helpers';
import { Click } from '../click';
import { Inline } from '../inline';

const Svg = _ref => {
  let {
    As = 'svg',
    extend,
    children,
    ...props
  } = _ref;
  const {
    css
  } = useFela(props);
  return /*#__PURE__*/React.createElement(As, _extends({
    className: css(extend)
  }, props), children);
};

const arrows = {
  left: 'M7 9.5l-4-4 4-4',
  right: 'M2 1.5l4 4-4 4',
  up: 'M1 6.5l4-4 4 4',
  down: 'M9 3.5l-4 4-4-4'
};

const Arrow = _ref2 => {
  let {
    direction,
    arrowPath,
    before
  } = _ref2;
  return /*#__PURE__*/React.createElement(Svg, {
    extend: { ...(before ? direction === 'rtl' ? {
        marginLeft: '0.55em'
      } : {
        marginRight: '0.55em'
      } : direction === 'rtl' ? {
        marginRight: '0.55em'
      } : {
        marginLeft: '0.55em'
      }),
      top: 1,
      position: 'relative'
    },
    preserveAspectRatio: "xMinYMin meet",
    viewBox: "0 0 11 11",
    width: "0.7851em",
    height: "0.7851em",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: arrowPath,
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }));
};

export const Link = /*#__PURE__*/forwardRef((_ref3, ref) => {
  let {
    children,
    arrow,
    ...props
  } = _ref3;
  const {
    theme
  } = useFela();
  const rel = getLinkRel({
    rel: props.rel,
    href: props.href,
    target: props.target
  });
  let resolveArrow = arrow;

  if (theme.direction === 'rtl') {
    if (arrow === 'right') {
      resolveArrow = 'left';
    }

    if (arrow === 'left') {
      resolveArrow = 'right';
    }
  }

  const arrowPath = arrows[resolveArrow || 'right'];
  return /*#__PURE__*/React.createElement(Click, _extends({
    ref: ref,
    rel: rel,
    href: props.href
  }, props, {
    extend: {
      fontSize: '0.875rem',
      fontWeight: 500,
      letterSpacing: '0.02em',
      padding: '12px 0',
      lineHeight: '1.375rem',
      fontFamily: theme.fontTypes.NOVUM,
      textTransform: 'uppercase',
      color: theme.tokens.linkForeground,
      stroke: theme.tokens.linkForeground,
      outlineColor: theme.tokens.linkForeground,
      outlineOffset: '0.25em',
      extend: [{
        condition: props.disabled,
        style: {
          opacity: 0.3
        }
      }, {
        condition: !props.disabled,
        style: {
          '@media (hover: hover)': {
            ':hover': {
              color: theme.tokens.linkHoverForeground,
              stroke: theme.tokens.linkHoverForeground,
              outlineColor: theme.tokens.linkHoverForeground
            }
          },
          ':focus': {
            color: theme.tokens.linkHoverForeground,
            stroke: theme.tokens.linkHoverForeground,
            outlineColor: theme.tokens.linkHoverForeground,
            ':active': {
              color: theme.tokens.linkPressForeground,
              stroke: theme.tokens.linkPressForeground,
              outlineColor: theme.tokens.linkPressForeground
            }
          },
          ':focus:not(:focus-visible):not(:active)': {
            color: theme.tokens.linkForeground,
            stroke: theme.tokens.linkForeground,
            outlineColor: theme.tokens.linkForeground
          }
        }
      }]
    }
  }), arrow && arrow === 'left' && /*#__PURE__*/React.createElement(Inline, {
    extend: {
      whiteSpace: 'nowrap'
    }
  }, /*#__PURE__*/React.createElement(Arrow, {
    before: true,
    arrowPath: arrowPath,
    direction: theme.direction
  }), "\uFEFF"), children, arrow && arrow !== 'left' && /*#__PURE__*/React.createElement(Inline, {
    extend: {
      whiteSpace: 'nowrap'
    }
  }, "\uFEFF", /*#__PURE__*/React.createElement(Arrow, {
    arrowPath: arrowPath,
    direction: theme.direction
  })));
});
Link.displayName = 'Link';
Link.propTypes = {
  /** A JSX node */
  children: PropTypes.node,
  href: PropTypes.string,
  arrow: PropTypes.oneOf(['up', 'down', 'right', 'left']),

  /** Disable link interaction */
  disabled: PropTypes.bool
};